import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, Car, Fuel, Calendar, Gauge, User, ChevronRight, 
  History, Wrench, X, Edit2, Save, Clock, Star, Receipt, 
  IndianRupee, CheckCircle2, AlertCircle, FileText
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Vehicles = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [vehicleStats, setVehicleStats] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchVehicles();
  }, [searchQuery]);

  // Handle navigation from Customers page
  useEffect(() => {
    if (location.state?.vehicleId && vehicles.length > 0) {
      const vehicle = vehicles.find(v => v.id === location.state.vehicleId);
      if (vehicle) {
        fetchVehicleDetails(vehicle);
      }
    }
  }, [location.state, vehicles]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('garage_token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/garage/vehicles/?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setVehicles(data.vehicles || []);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleDetails = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setLoadingDetails(true);
    setEditing(false);
    setEditForm(vehicle);
    
    try {
      const token = localStorage.getItem('garage_token');
      
      const response = await fetch(`${API_URL}/api/garage/vehicles/${vehicle.id}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVehicleHistory(data.history || []);
        setVehicleStats(data.statistics || null);
        // Update vehicle with latest data
        if (data.vehicle) {
          setSelectedVehicle(data.vehicle);
          setEditForm(data.vehicle);
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveVehicle = async () => {
    try {
      const token = localStorage.getItem('garage_token');
      const response = await fetch(`${API_URL}/api/garage/vehicles/${selectedVehicle.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      const data = await response.json();
      if (data.success) {
        setSelectedVehicle(data.vehicle);
        setEditing(false);
        toast.success('Vehicle updated');
        fetchVehicles();
      }
    } catch (error) {
      toast.error('Failed to update vehicle');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'delivered': 'bg-green-500',
      'dropped': 'bg-green-500',
      'work_in_progress': 'bg-blue-500',
      'pending_estimate': 'bg-yellow-500',
      'estimate_sent': 'bg-orange-500',
      'customer_approved': 'bg-emerald-500',
      'qc': 'bg-purple-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className={`min-h-screen ${theme.pageBg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.textPrimary}`}>Vehicles</h1>
            <p className={theme.textSecondary}>Track vehicle service history</p>
          </div>
          
          {/* Search */}
          <div className="relative w-full md:w-80">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
            <Input
              placeholder="Search by registration, make, model..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 ${theme.inputBg}`}
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Vehicle List */}
          <div className="lg:col-span-1 space-y-4">
            <Card className={theme.cardBg}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-lg ${theme.textPrimary}`}>
                  <Car className="w-5 h-5 inline mr-2" />
                  All Vehicles ({vehicles.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : vehicles.length === 0 ? (
                  <div className={`text-center py-8 ${theme.textMuted}`}>
                    <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No vehicles found</p>
                    <p className="text-sm">Vehicles are auto-created from job cards</p>
                  </div>
                ) : (
                  vehicles.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      onClick={() => fetchVehicleDetails(vehicle)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedVehicle?.id === vehicle.id 
                          ? 'bg-orange-500/20 border border-orange-500/50' 
                          : `${theme.hoverBg} border border-transparent`
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-mono font-bold ${theme.textPrimary}`}>
                            {vehicle.registration_number}
                          </p>
                          <p className={`text-sm ${theme.textMuted}`}>
                            {vehicle.make} {vehicle.model}
                          </p>
                          {vehicle.customer && (
                            <p className={`text-xs ${theme.textMuted}`}>
                              <User className="w-3 h-3 inline mr-1" />
                              {vehicle.customer.name}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`text-xs ${theme.textMuted}`}>{vehicle.total_jobs} services</p>
                          {vehicle.last_service_date && (
                            <p className={`text-xs ${theme.textMuted}`}>
                              Last: {formatDate(vehicle.last_service_date)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Vehicle Details */}
          <div className="lg:col-span-2">
            {selectedVehicle ? (
              <div className="space-y-4">
                {/* Vehicle Info Card */}
                <Card className={theme.cardBg}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className={theme.textPrimary}>Vehicle Details</CardTitle>
                    <div className="flex gap-2">
                      {editing ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveVehicle}>
                            <Save className="w-4 h-4 mr-1" /> Save
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                          <Edit2 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setSelectedVehicle(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editing ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Registration Number</label>
                          <Input
                            value={editForm.registration_number || ''}
                            onChange={(e) => setEditForm({...editForm, registration_number: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Make</label>
                          <Input
                            value={editForm.make || ''}
                            onChange={(e) => setEditForm({...editForm, make: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Model</label>
                          <Input
                            value={editForm.model || ''}
                            onChange={(e) => setEditForm({...editForm, model: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Year</label>
                          <Input
                            type="number"
                            value={editForm.year || ''}
                            onChange={(e) => setEditForm({...editForm, year: parseInt(e.target.value) || null})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Fuel Type</label>
                          <Input
                            value={editForm.fuel_type || ''}
                            onChange={(e) => setEditForm({...editForm, fuel_type: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Odometer Reading</label>
                          <Input
                            value={editForm.odometer_reading || ''}
                            onChange={(e) => setEditForm({...editForm, odometer_reading: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Vehicle Header */}
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-xl bg-orange-500/20 flex items-center justify-center">
                            <Car className="w-8 h-8 text-orange-500" />
                          </div>
                          <div>
                            <p className={`font-mono text-2xl font-bold ${theme.textPrimary}`}>
                              {selectedVehicle.registration_number}
                            </p>
                            <p className={`text-lg ${theme.textSecondary}`}>
                              {selectedVehicle.make} {selectedVehicle.model} {selectedVehicle.year && `(${selectedVehicle.year})`}
                            </p>
                          </div>
                        </div>

                        {/* Vehicle Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className={`p-3 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
                            <Fuel className={`w-5 h-5 mb-1 ${theme.textMuted}`} />
                            <p className={`text-sm ${theme.textMuted}`}>Fuel</p>
                            <p className={`font-medium ${theme.textPrimary}`}>
                              {selectedVehicle.fuel_type || 'N/A'}
                            </p>
                          </div>
                          <div className={`p-3 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
                            <Gauge className={`w-5 h-5 mb-1 ${theme.textMuted}`} />
                            <p className={`text-sm ${theme.textMuted}`}>Odometer</p>
                            <p className={`font-medium ${theme.textPrimary}`}>
                              {selectedVehicle.odometer_reading || 'N/A'}
                            </p>
                          </div>
                          <div className={`p-3 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
                            <Wrench className={`w-5 h-5 mb-1 ${theme.textMuted}`} />
                            <p className={`text-sm ${theme.textMuted}`}>Total Services</p>
                            <p className={`font-medium ${theme.textPrimary}`}>
                              {selectedVehicle.total_jobs}
                            </p>
                          </div>
                          <div className={`p-3 rounded-lg ${theme.cardBgSolid} border ${theme.border}`}>
                            <Clock className={`w-5 h-5 mb-1 ${theme.textMuted}`} />
                            <p className={`text-sm ${theme.textMuted}`}>Last Service</p>
                            <p className={`font-medium ${theme.textPrimary}`}>
                              {formatDate(selectedVehicle.last_service_date)}
                            </p>
                          </div>
                        </div>

                        {/* Owner Info */}
                        {selectedVehicle.customer && (
                          <div className={`p-3 rounded-lg border ${theme.border} ${theme.hoverBg} cursor-pointer`}
                               onClick={() => navigate('/garage/customers')}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                                  <User className="w-5 h-5 text-blue-500" />
                                </div>
                                <div>
                                  <p className={`font-medium ${theme.textPrimary}`}>
                                    {selectedVehicle.customer.name}
                                  </p>
                                  <p className={`text-sm ${theme.textMuted}`}>
                                    {selectedVehicle.customer.phone}
                                  </p>
                                </div>
                              </div>
                              <ChevronRight className={`w-5 h-5 ${theme.textMuted}`} />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Service History */}
                <Card className={theme.cardBg}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className={`text-lg ${theme.textPrimary}`}>
                        <History className="w-5 h-5 inline mr-2" />
                        Service History ({vehicleHistory.length})
                      </CardTitle>
                      {vehicleStats && (
                        <div className="flex items-center gap-4 text-sm">
                          <span className={theme.textSecondary}>
                            Total Spent: <span className="text-green-400 font-bold">₹{vehicleStats.total_spent?.toLocaleString() || 0}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingDetails ? (
                      <div className="text-center py-4">
                        <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto" />
                      </div>
                    ) : vehicleHistory.length === 0 ? (
                      <p className={`text-center py-4 ${theme.textMuted}`}>No service history</p>
                    ) : (
                      <div className="space-y-4">
                        {vehicleHistory.map((record, index) => (
                          <div 
                            key={record.job_id || index}
                            className={`p-4 rounded-lg border ${theme.border} ${theme.pageBg}`}
                          >
                            {/* Header Row */}
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`w-2 h-2 rounded-full ${getStatusColor(record.status)}`} />
                                  <span 
                                    className={`font-medium ${theme.textPrimary} cursor-pointer hover:text-orange-400`}
                                    onClick={() => navigate(`/garage/jobs/${record.job_id}`)}
                                  >
                                    {record.job_number}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {record.job_type === 'pickup' ? 'Pickup' : 'Walk-in'}
                                  </Badge>
                                  {record.status === 'closed' && (
                                    <Badge className="bg-green-500/20 text-green-400 text-xs">Completed</Badge>
                                  )}
                                </div>
                                <div className={`flex items-center gap-4 text-sm ${theme.textSecondary}`}>
                                  <span>
                                    <Calendar className="w-3 h-3 inline mr-1" />
                                    {formatDate(record.date)}
                                  </span>
                                  {record.odometer_reading && (
                                    <span>
                                      <Gauge className="w-3 h-3 inline mr-1" />
                                      {record.odometer_reading.toLocaleString()} km
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                {record.estimate_total > 0 && (
                                  <p className="text-green-400 font-bold text-lg">
                                    ₹{record.estimate_total.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Customer Complaint */}
                            {record.customer_complaint && (
                              <div className={`p-2 rounded ${theme.cardBg} border ${theme.border} mb-3`}>
                                <p className={`text-xs ${theme.textSecondary} mb-1`}>Customer Complaint:</p>
                                <p className={`text-sm ${theme.textPrimary} italic`}>"{record.customer_complaint}"</p>
                              </div>
                            )}
                            
                            {/* Services Done */}
                            {record.services_done && record.services_done.length > 0 && (
                              <div className="mb-3">
                                <p className={`text-xs ${theme.textSecondary} mb-2`}>Services Performed:</p>
                                <div className="grid gap-1">
                                  {record.services_done.map((service, idx) => (
                                    <div 
                                      key={idx}
                                      className={`flex items-center justify-between text-sm px-2 py-1 rounded ${theme.cardBg}`}
                                    >
                                      <span className={theme.textPrimary}>
                                        {service.type === 'part' ? '🔧' : service.type === 'body_shop_labour' ? '🚗' : '👨‍🔧'} 
                                        {' '}{service.description}
                                        {service.quantity > 1 && <span className={theme.textSecondary}> x{service.quantity}</span>}
                                      </span>
                                      <span className="text-green-400">₹{service.amount?.toLocaleString()}</span>
                                    </div>
                                  ))}
                                </div>
                                <div className={`flex justify-between mt-2 pt-2 border-t ${theme.border} text-sm`}>
                                  <span className={theme.textSecondary}>Parts: ₹{record.parts_total?.toLocaleString() || 0}</span>
                                  <span className={theme.textSecondary}>Labour: ₹{record.labour_total?.toLocaleString() || 0}</span>
                                </div>
                              </div>
                            )}
                            
                            {/* Invoice Info */}
                            {record.invoice && (
                              <div className={`flex items-center justify-between p-2 rounded ${theme.cardBg} border ${theme.border} mb-3`}>
                                <div className="flex items-center gap-2">
                                  <Receipt className="w-4 h-4 text-blue-400" />
                                  <span className={`text-sm ${theme.textPrimary}`}>
                                    Invoice: {record.invoice.number}
                                  </span>
                                  {record.invoice.gst > 0 && (
                                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                      GST: ₹{record.invoice.gst.toLocaleString()}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`font-bold ${theme.textPrimary}`}>
                                    ₹{record.invoice.amount?.toLocaleString()}
                                  </span>
                                  {record.invoice.paid ? (
                                    <Badge className="bg-green-500/20 text-green-400">
                                      <CheckCircle2 className="w-3 h-3 mr-1" />Paid
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-amber-500/20 text-amber-400">
                                      <AlertCircle className="w-3 h-3 mr-1" />Unpaid
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Rating/Feedback */}
                            {record.rating && (
                              <div className={`p-2 rounded ${theme.cardBg} border ${theme.border}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm ${theme.textSecondary}`}>Customer Rating:</span>
                                    <div className="flex items-center gap-1">
                                      {[1,2,3,4,5].map((star) => (
                                        <Star 
                                          key={star}
                                          className={`w-4 h-4 ${star <= record.rating.overall ? 'fill-yellow-400 text-yellow-400' : 'fill-slate-600 text-slate-600'}`}
                                        />
                                      ))}
                                    </div>
                                    <span className={`text-sm font-bold ${theme.textPrimary}`}>{record.rating.overall}/5</span>
                                  </div>
                                  <span className={`text-xs ${theme.textSecondary}`}>{formatDate(record.rating.date)}</span>
                                </div>
                                {record.rating.review && (
                                  <p className={`text-sm ${theme.textPrimary} italic mb-2`}>"{record.rating.review}"</p>
                                )}
                                <div className="flex flex-wrap gap-3 text-xs">
                                  {record.rating.service_quality && (
                                    <span className={theme.textSecondary}>
                                      Service: {'⭐'.repeat(record.rating.service_quality)}
                                    </span>
                                  )}
                                  {record.rating.communication && (
                                    <span className={theme.textSecondary}>
                                      Communication: {'⭐'.repeat(record.rating.communication)}
                                    </span>
                                  )}
                                  {record.rating.value_for_money && (
                                    <span className={theme.textSecondary}>
                                      Value: {'⭐'.repeat(record.rating.value_for_money)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* View Job Link */}
                            <div className="flex justify-end mt-3">
                              <button 
                                onClick={() => navigate(`/garage/jobs/${record.job_id}`)}
                                className={`text-sm text-orange-400 hover:text-orange-300 flex items-center gap-1`}
                              >
                                View Job Details <ChevronRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className={`${theme.cardBg} h-full flex items-center justify-center`}>
                <CardContent className="text-center py-16">
                  <Car className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted} opacity-50`} />
                  <p className={theme.textMuted}>Select a vehicle to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Vehicles;
